import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon, UsersIcon, UserIcon, ClipboardIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, BuildingOffice2Icon, DocumentTextIcon, ClockIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useAuth } from './App.js';

const menu = [
  { to: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { to: '/Clients', label: 'Clients', icon: UsersIcon },
  { to: '/sessions', label: 'Sessions', icon: ClockIcon },
  { to: '/users', label: 'Users', icon: UserIcon },
  { to: '/companies', label: 'Companies', icon: BuildingOffice2Icon },
  { to: '/reports', label: 'Reports', icon: ClipboardIcon },
  { to: '/emr-types', label: 'EMR Types', icon: DocumentTextIcon },
  { to: '/settings', label: 'Settings', icon: Cog6ToothIcon },
];

const Layout = ({ children, pageTitle }) => {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  if (!user) return children;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen ${collapsed ? 'w-20' : 'w-60'} bg-white shadow-lg flex flex-col items-center py-8 z-30 transition-all duration-200`}>
        {/* Collapse/Expand Button */}
        <button
          className="absolute top-4 right-2 w-8 h-8 flex items-center justify-center rounded hover:bg-blue-100 transition"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronDoubleRightIcon className="w-6 h-6 text-blue-600" /> : <ChevronDoubleLeftIcon className="w-6 h-6 text-blue-600" />}
        </button>
        {/* Logo */}
        <div className={`bg-blue-600 text-white flex items-center justify-center font-bold ${collapsed ? 'w-12 h-12 rounded-xl mb-8 mt-8' : 'w-14 h-14 rounded-2xl mb-12'}`}>N</div>
        {/* Menu */}
        <nav className={`flex flex-col gap-2 w-full items-center ${collapsed ? 'mt-2' : 'mt-0'}`}>
          {menu.filter(item => {
            // Role 3 (Super Admin) - can see everything
            if (user && user.role_id === 3) {
              return true;
            }
            
            // Role 1 (Admin) - can see Clients, Sessions, Users
            if (user && user.role_id === 1) {
              return ['Clients', 'Sessions', 'Users'].includes(item.label);
            }
            
            // Role 2 (Standard) - can see only Clients, Sessions
            if (user && user.role_id === 2) {
              return ['Clients', 'Sessions'].includes(item.label);
            }
            
            // No access for other roles or no user
            return false;
          }).map(({ to, label, icon: Icon }) => {
            const isActive = location.pathname === to || 
              (to === '/Clients' && location.pathname.startsWith('/Clients/')) ||
              (to === '/sessions' && location.pathname.startsWith('/sessions/')) ||
              (to === '/users' && location.pathname.startsWith('/user/')) ||
              (to === '/companies' && location.pathname.startsWith('/companies/')) ||
              (to === '/emr-types' && location.pathname.startsWith('/emr-types/')) ||
              (to === '/settings' && location.pathname.startsWith('/settings/'));
            
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start'} w-11/12 py-2 mb-1 rounded-lg text-lg font-medium transition text-center group ${
                  isActive 
                    ? 'bg-blue-100 text-blue-700 shadow-sm' 
                    : 'text-slate-800 hover:bg-blue-100 hover:text-blue-700'
                }`}
              >
                <Icon className={`w-6 h-6 mr-0.5 group-hover:text-blue-700 ${
                  isActive ? 'text-blue-700' : 'text-blue-500'
                }`} />
                {!collapsed && <span className="ml-3">{label}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>
      {/* Main area (with left margin for sidebar) */}
      <div className={`flex-1 min-h-screen ${collapsed ? 'ml-20' : 'ml-60'} flex flex-col transition-all duration-200`}>
        {/* Header */}
        <header className="h-20 flex items-center justify-between px-10 bg-white shadow-sm border-b border-slate-200 sticky top-0 z-20">
          {/* Center: Page Title */}
          <div className="absolute left-1/2 transform -translate-x-1/2 text-2xl font-bold text-slate-700 select-none">
            {pageTitle || "Dashboard"}
          </div>
          {/* Right: Profile */}
          <div className="relative ml-auto" ref={dropdownRef}>
            <button
              className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white font-semibold text-xl focus:outline-none shadow-lg hover:shadow-xl transition-all duration-200 border-2 border-white"
              onClick={() => setShowDropdown((v) => !v)}
            >
              {user?.full_name ? user.full_name[0].toUpperCase() : "U"}
            </button>
            {showDropdown && (
              <div className="absolute right-0 mt-3 bg-white shadow-xl rounded-lg min-w-[160px] py-2 z-40 border border-slate-100">
                <div className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-slate-700" onClick={() => { setShowDropdown(false); navigate("/profile"); }}>Profile</div>
                <div className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-slate-700" onClick={() => { setShowDropdown(false); navigate("/settings"); }}>Settings</div>
                <div className="px-4 py-2 hover:bg-red-50 cursor-pointer text-red-500" onClick={() => { setShowDropdown(false); logout(); }}>Logout</div>
              </div>
            )}
          </div>
        </header>
        {/* Main content area */}
        <div className="m-4 max-w-screen-2xl mx-auto w-full">
          <main className="bg-white min-h-[60vh] p-4 w-full shadow-lg rounded-2xl">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout; 